import { PageContentTypes } from '@fm-nx/constants';
import {
    TypePageCaseStudy,
    TypePageCompany,
    TypePageExpertise,
    TypePageIndustry,
    TypePageJournal,
    TypePageLanding,
    TypePagePerson,
    TypePageService,
    TypePageTechnology,
} from '@fm-nx/contentful-types';
import { EntrySys, OrderFilterPaths } from 'contentful';
import { getClient } from './client';
import { parsePage } from './pageParsers';

type GetPageByIdProps = {
    id: string | string[];
    preview?: boolean;
};

export const getPageById = async <TypePage>(props: GetPageByIdProps): Promise<TypePage | null> => {
    const { id, preview = false } = props;

    const page = (await getClient(preview).withoutUnresolvableLinks.getEntry(id as string)) as TypePage;
    return page || null;
};

type GetPageBySlugProps = {
    slug: string | string[];
    preview?: boolean;
};

export const getPageBySlug = async <TypePage>(props: GetPageBySlugProps): Promise<TypePage | null> => {
    const { slug, preview = false } = props;
    const query = {
        limit: 1,
        locale: 'en-US',
        'fields.slug': slug,
    };

    const response = await getClient(preview).withoutUnresolvableLinks.getEntries(query);

    const [page] = response.items as TypePage[];
    return page || null;
};

type GetPageProps = {
    pageContentType: string;
    slug: string;
    preview?: boolean;
    include?: number;
};

export const getPage = async <TypePage>(props: GetPageProps): Promise<TypePage | null> => {
    const { slug, pageContentType, preview = false, include = 4 } = props;
    const query = {
        limit: 1,
        include,
        locale: 'en-US',
        'fields.slug': slug,
        content_type: pageContentType,
    };

    const response = await getClient(preview).withoutUnresolvableLinks.getEntries(query);

    const [page] = response.items as TypePage[];
    return page || null;
};

// Get pages by content type
type GetPagesProps = {
    preview?: boolean;
    limit?: number;
    order?: [OrderFilterPaths<EntrySys, 'sys'>] | string[];
};

export const getPagesTechnology = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageTechnology<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Technology,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};
export const getPagesService = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageService<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Service,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};

export const getPagesExpertise = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageExpertise<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Expertise,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};

export const getPagesIndustry = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageIndustry<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Industry,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};

export const getPagesCaseStudy = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageCaseStudy<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.CaseStudy,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};

type GetOnePagePersonProps = {
    slug: string;
    preview?: boolean;
    include?: number;
};

export const getOnePagePerson = async <TypePagePerson>(
    props: GetOnePagePersonProps
): Promise<TypePagePerson | null> => {
    const { slug, preview = false, include = 4 } = props;
    const query = {
        limit: 1,
        include,
        locale: 'en-US',
        'fields.slug': slug,
        'fields.isActive': true,
        content_type: PageContentTypes.Person,
    };

    const response = await getClient(preview).withoutUnresolvableLinks.getEntries(query);

    const [page] = response.items as TypePagePerson[];
    return page || null;
};
export const getPagesPerson = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePagePerson<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Person,
        'fields.isActive': true,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};

export const getPagesJournal = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageJournal<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Journal,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};

export const getPagesLanding = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageLanding<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Landing,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};

export const getPagesCompany = async ({
    preview = false,
    limit = 100,
    order = ['-sys.createdAt'],
}: GetPagesProps = {}): Promise<TypePageCompany<'WITHOUT_UNRESOLVABLE_LINKS'>[]> => {
    const response = await getClient(preview).withoutUnresolvableLinks.getEntries({
        limit,
        include: 10,
        content_type: PageContentTypes.Company,
        order: order as any, // Contentful SDK doesn't support fields.* in types, but API does
    });

    return response.items ? response.items.map((page) => parsePage(page)) : [];
};
